
import React, { useEffect, useState } from "react";
import BeginnerSidebar from "./BeginnerSidebar";
import {
  getPracticeSets,
  startPracticeAttempt,
  submitPracticeAttempt,
  getPracticeAttemptReview,
} from "../api/practiceClient";
import "../styles/practice.css";

export default function BeginnerPractice() {
  const [practiceSets, setPracticeSets] = useState([]);
  const [selectedSetId, setSelectedSetId] = useState("");
  const [attempt, setAttempt] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [started, setStarted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let active = true;

    async function loadPracticeSets() {
      setLoading(true);
      setError("");

      try {
        const response = await getPracticeSets();
        const data = response.data;
        const items = Array.isArray(data)
          ? data
          : data.results || [];

        if (active) {
          setPracticeSets(items);

          if (items.length) {
            setSelectedSetId(String(items[0].id));
          }
        }
      } catch (err) {
        if (active) {
          setError(
            err.response?.data?.detail ||
              "Unable to load practice sets. Check your connection and login."
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    loadPracticeSets();

    return () => {
      active = false;
    };
  }, []);

  function getQuestionId(answer) {
    return answer.question ?? answer.question_id ?? answer.id;
  }

  async function startPractice() {
    if (!selectedSetId) {
      setError("Please select a practice set.");
      return;
    }

    setLoading(true);
    setError("");
    setAttempt(null);
    setAnswers({});
    setResult(null);
    setSubmitted(false);
    setStarted(false);
    setCurrentIndex(0);

    try {
      const response = await startPracticeAttempt(selectedSetId);
      const data = response.data;

      if (!Array.isArray(data.answers) || !data.answers.length) {
        setError("The backend returned an attempt without questions.");
        return;
      }

      setAttempt(data);
      setStarted(true);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Unable to start this practice attempt."
      );
    } finally {
      setLoading(false);
    }
  }

  function chooseAnswer(question, optionId) {
    const questionId = getQuestionId(question);

    setAnswers((previous) => ({
      ...previous,
      [questionId]: optionId,
    }));

    setError("");
  }

  async function finishPractice() {
    if (!attempt?.id) return;

    setLoading(true);
    setError("");

    const payload = (attempt.answers || []).map((answer) => {
      const questionId = getQuestionId(answer);

      return {
        question: questionId,
        selected_option_id: answers[questionId] ?? null,
        answer_text: "",
      };
    });

    try {
      const response = await submitPracticeAttempt(
        attempt.id,
        payload
      );

      setResult(response.data);
      setSubmitted(true);

      try {
        const reviewResponse = await getPracticeAttemptReview(
          attempt.id
        );

        // Use the dedicated review response for question and answer data.
        setResult({
          ...response.data,
          ...reviewResponse.data,
        });
      } catch (reviewError) {
        setError(
          reviewError.response?.data?.detail ||
            "Your practice was submitted, but answer review could not be loaded."
        );
      }
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Unable to submit the practice attempt. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }

  function resetPractice() {
    setAttempt(null);
    setAnswers({});
    setResult(null);
    setStarted(false);
    setSubmitted(false);
    setCurrentIndex(0);
    setError("");
  }

  const questions = attempt?.answers || [];
  const question = questions[currentIndex];
  const options = question?.options || [];

  function isCorrectOption(option) {
    return (
      option?.is_correct === true ||
      option?.is_correct === 1 ||
      option?.is_correct === "true"
    );
  }

  function getOptionLabel(option, index) {
    return (
      option?.label ||
      String.fromCharCode(65 + index)
    );
  }

  function formatOption(option, index) {
    if (!option) return "No answer provided";

    const label = getOptionLabel(option, index);
    const text = option.option_text ?? option.text ?? "";

    return `${label}. ${text}`;
  }

  function getQuestionText(item) {
    const value =
      item.question_text ??
      item.question?.question_text ??
      item.question?.text ??
      item.text ??
      "";

    return typeof value === "string"
      ? value
      : "Question text unavailable";
  }

  function getSelectedOption(item, itemOptions) {
    const selectedId =
      item.selected_option_id ??
      item.selected_option?.id ??
      item.option_selected_id;

    if (selectedId !== null && selectedId !== undefined) {
      const match = itemOptions.find(
        (option) => String(option.id) === String(selectedId)
      );

      if (match) return match;
    }

    if (
      item.selected_option &&
      typeof item.selected_option === "object"
    ) {
      return item.selected_option;
    }

    return null;
  }

  function getCorrectOption(item, itemOptions) {
    // Prefer the explicit correct_option returned by the review API.
    if (
      item.correct_option &&
      typeof item.correct_option === "object"
    ) {
      return item.correct_option;
    }

    // Otherwise use the correct flag on the options themselves.
    return itemOptions.find(isCorrectOption) || null;
  }

  function getStudentAnswer(item, itemOptions) {
    const selectedOption = getSelectedOption(item, itemOptions);

    if (selectedOption) {
      const index = itemOptions.findIndex(
        (option) => String(option.id) === String(selectedOption.id)
      );

      return formatOption(
        selectedOption,
        index >= 0 ? index : 0
      );
    }

    const answerText =
      item.your_answer ??
      item.selected_answer ??
      item.answer_text ??
      item.selected_option_text;

    if (typeof answerText === "string" && answerText.trim()) {
      return answerText;
    }

    if (answerText && typeof answerText === "object") {
      return (
        answerText.option_text ??
        answerText.text ??
        answerText.label ??
        "No answer provided"
      );
    }

    return "No answer provided";
  }

  function getCorrectOptionText(item, itemOptions) {
    const correctOption = getCorrectOption(item, itemOptions);

    if (correctOption) {
      const index = itemOptions.findIndex(
        (option) => String(option.id) === String(correctOption.id)
      );

      return formatOption(
        correctOption,
        index >= 0 ? index : 0
      );
    }

    // Supports text-answer questions when the backend has an answer.
    const storedAnswer =
      item.correct_answer_text ??
      item.correct_answer ??
      item.answer_key ??
      item.expected_answer;

    if (typeof storedAnswer === "string" && storedAnswer.trim()) {
      return storedAnswer;
    }

    return "No correct option configured";
  }

  return (
    <div className="beginner-portal">
      <BeginnerSidebar />

      <main className="beginner-portal-content">
        <header className="beginner-page-header">
          <div>
            <p className="beginner-eyebrow">
              BEGINNER STUDENT PORTAL
            </p>
            <h1>Practice Exams</h1>
            <p>
              Practice at your own pace before attempting the main exam.
            </p>
          </div>
        </header>

        {!started && (
          <section className="beginner-practice-card">
            <h2>Start a practice session</h2>

            <p>
              Choose an available practice set. Your result will be
              calculated by the server when you submit.
            </p>

            <p className="beginner-practice-review-note">
              Practice sets require an active subscription for your
              class. Answer review becomes available after submission.
            </p>

            <label htmlFor="practice-set">Practice set</label>

            <select
              id="practice-set"
              value={selectedSetId}
              onChange={(event) =>
                setSelectedSetId(event.target.value)
              }
              disabled={loading || !practiceSets.length}
            >
              {practiceSets.length === 0 && (
                <option value="">
                  No practice sets available
                </option>
              )}

              {practiceSets.map((set) => (
                <option key={set.id} value={String(set.id)}>
                  {set.title} —{" "}
                  {set.exam_name || set.section_name || "Practice"}{" "}
                  ({set.question_count} questions)
                </option>
              ))}
            </select>

            {error && (
              <p className="beginner-practice-error">{error}</p>
            )}

            <button
              type="button"
              onClick={startPractice}
              disabled={loading || !selectedSetId}
            >
              {loading ? "Please wait..." : "Start Practice"}
            </button>
          </section>
        )}

        {started && !submitted && question && (
          <section className="beginner-practice-card">
            <div className="beginner-practice-progress">
              Question {currentIndex + 1} of {questions.length}
            </div>

            <h2>
              {question.question_text ||
                `Question ${currentIndex + 1}`}
            </h2>

            {options.length > 0 ? (
              <div className="beginner-practice-options">
                {options.map((option, index) => (
                  <label
                    className="beginner-practice-option"
                    key={option.id ?? index}
                  >
                    <input
                      type="radio"
                      name={`question-${getQuestionId(question)}`}
                      checked={
                        String(
                          answers[getQuestionId(question)] ?? ""
                        ) === String(option.id)
                      }
                      onChange={() =>
                        chooseAnswer(question, option.id)
                      }
                    />

                    <span>
                      {option.option_text ||
                        option.text ||
                        `Option ${index + 1}`}
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              <p>No options are available for this question.</p>
            )}

            {error && (
              <p className="beginner-practice-error">{error}</p>
            )}

            <div className="beginner-practice-actions">
              <button
                type="button"
                disabled={currentIndex === 0 || loading}
                onClick={() =>
                  setCurrentIndex((index) => index - 1)
                }
              >
                Previous
              </button>

              {currentIndex < questions.length - 1 ? (
                <button
                  type="button"
                  disabled={loading}
                  onClick={() =>
                    setCurrentIndex((index) => index + 1)
                  }
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  disabled={loading}
                  onClick={finishPractice}
                >
                  {loading ? "Submitting..." : "Finish Practice"}
                </button>
              )}
            </div>
          </section>
        )}

        {submitted && result && (
          <section className="beginner-practice-card">
            <h2>Practice session completed!</h2>

            <p>
              Score: <strong>{result.score ?? 0}</strong> /{" "}
              <strong>{result.total_marks ?? 0}</strong>
            </p>

            <p>
              Answered:{" "}
              {
                Object.values(answers).filter(
                  (value) => value != null
                ).length
              }{" "}
              of {questions.length}
            </p>

            {error && (
              <p className="beginner-practice-error">{error}</p>
            )}

            {Array.isArray(result.answers) &&
              result.answers.length > 0 && (
                <section
                  className="beginner-practice-review"
                  aria-label="Practice answer review"
                >
                  <h3>Answer Review</h3>

                  <p className="beginner-practice-review-note">
                    Review is available because you submitted this
                    practice set. Main exam answers are not shown here.
                  </p>

                  {result.answers.map((item, index) => {
                    const itemOptions = Array.isArray(item.options)
                      ? item.options
                      : [];

                    const selectedOption = getSelectedOption(
                      item,
                      itemOptions
                    );

                    const correctOption = getCorrectOption(
                      item,
                      itemOptions
                    );

                    const studentAnswer = getStudentAnswer(
                      item,
                      itemOptions
                    );

                    const correctAnswerText =
                      getCorrectOptionText(item, itemOptions);

                    const awardedMarks =
                      item.marks_awarded ??
                      item.awarded_marks ??
                      item.score ??
                      0;

                    const maximumMarks =
                      item.marks ??
                      item.maximum_marks ??
                      item.max_marks ??
                      0;

                    return (
                      <article
                        className="beginner-practice-review-item"
                        key={item.question_id ?? index}
                      >
                        <div className="beginner-practice-review-heading">
                          <strong>Question {index + 1}</strong>
                        </div>

                        <p className="beginner-practice-review-question">
                          {getQuestionText(item)}
                        </p>

                        {itemOptions.length > 0 && (
                          <div className="beginner-practice-review-options">
                            {itemOptions.map(
                              (option, optionIndex) => {
                                const isSelected =
                                  selectedOption &&
                                  String(option.id) ===
                                    String(selectedOption.id);

                                return (
                                  <div
                                    key={option.id ?? optionIndex}
                                    className={
                                      isSelected
                                        ? "review-option-selected"
                                        : ""
                                    }
                                  >
                                    <span>
                                      {formatOption(
                                        option,
                                        optionIndex
                                      )}
                                    </span>
                                  </div>
                                );
                              }
                            )}
                          </div>
                        )}

                        <div className="beginner-practice-review-answer-summary">
                          <div>
                            <strong>Your Answer:</strong>
                            <p>{studentAnswer}</p>
                          </div>

                          <div>
                            <strong>Correct Option:</strong>
                            <p>{correctAnswerText}</p>
                          </div>
                        </div>

                        <p className="beginner-practice-review-marks">
                          <strong>
                            Marks: {awardedMarks} / {maximumMarks}
                          </strong>
                        </p>

                        {item.explanation && (
                          <p className="beginner-practice-review-explanation">
                            <strong>Explanation:</strong>{" "}
                            {item.explanation}
                          </p>
                        )}
                      </article>
                    );
                  })}
                </section>
              )}

            <button type="button" onClick={resetPractice}>
              Start Another Practice
            </button>
          </section>
        )}
      </main>
    </div>
  );
}
